/**
 * F-508-FE-03: HospitalDetailPage
 *
 * Hospital detail view at /admin/hospitals/:id.
 * - Header: hospital name, status badge, doctor count, sessions count, registration URL + copy
 * - Breadcrumb: Dashboard > [Hospital Name]
 * - 6-tab interface (lazy-loaded): Doctors, Live Sessions, Analytics, Error Logs, Billing, Settings
 *
 * API: GET /admin/hospitals/:id (for header data)
 */

import { useState, useEffect, Suspense, lazy } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AdminLayout, type BreadcrumbItem } from '@/layouts/AdminLayout';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Lazy-loaded tabs
// ---------------------------------------------------------------------------

const DoctorManagementTab = lazy(() =>
  import('./tabs/DoctorManagementTab').then((m) => ({ default: m.DoctorManagementTab })),
);
const LiveSessionsTab = lazy(() =>
  import('./tabs/LiveSessionsTab').then((m) => ({ default: m.LiveSessionsTab })),
);
const AnalyticsTab = lazy(() =>
  import('./tabs/AnalyticsTab').then((m) => ({ default: m.AnalyticsTab })),
);
const ErrorLogsTab = lazy(() =>
  import('./tabs/ErrorLogsTab').then((m) => ({ default: m.ErrorLogsTab })),
);
const BillingTab = lazy(() =>
  import('./tabs/BillingTab').then((m) => ({ default: m.BillingTab })),
);
const SettingsTab = lazy(() =>
  import('./tabs/SettingsTab').then((m) => ({ default: m.SettingsTab })),
);
const CompliancePrivacyTab = lazy(() =>
  import('./tabs/CompliancePrivacyTab').then((m) => ({ default: m.CompliancePrivacyTab })),
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface HospitalDetail {
  id: string;
  name: string;
  status: 'ACTIVE' | 'INACTIVE';
  activeDoctors: number;
  totalDoctors: number;
  activeSessions: number;
  registrationUrl: string;
  billingModel: 'CONSUMPTION' | 'SUBSCRIPTION';
}

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

type TabId = 'doctors' | 'sessions' | 'analytics' | 'errors' | 'billing' | 'settings' | 'compliance';

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'doctors', label: 'Doctors' },
  { id: 'sessions', label: 'Live Sessions' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'errors', label: 'Error Logs' },
  { id: 'billing', label: 'Billing' },
  { id: 'settings', label: 'Settings' },
  { id: 'compliance', label: 'Compliance & Privacy' },
];

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: 'ACTIVE' | 'INACTIVE' }) {
  if (status === 'ACTIVE') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200">
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
      Inactive
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tab loading fallback
// ---------------------------------------------------------------------------

function TabSkeleton() {
  return (
    <div className="space-y-3 py-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="skeleton h-10 w-full rounded-clinical" />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function HospitalDetailPage() {
  const { id: hospitalId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [copiedUrl, setCopiedUrl] = useState(false);

  // Read active tab from URL search param (supports direct-link to tab)
  const tabParam = searchParams.get('tab') as TabId | null;
  const [activeTab, setActiveTab] = useState<TabId>(
    tabParam && TABS.some((t) => t.id === tabParam) ? tabParam : 'doctors',
  );

  // Keep URL in sync with active tab
  useEffect(() => {
    if (activeTab !== 'doctors') {
      setSearchParams({ tab: activeTab }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [activeTab, setSearchParams]);

  const { data: hospital, isLoading, refetch } = useQuery<HospitalDetail>({
    queryKey: ['admin', 'hospitals', hospitalId, 'header'],
    queryFn: async () => {
      const res = await api.get<{ data: {
        id: string;
        name: string;
        slug: string;
        status: string;
        maxDoctorCount: number;
        doctorCount: number;
        activeDoctorCount: number;
        activeSessionCount: number;
        billingModel?: 'CONSUMPTION' | 'SUBSCRIPTION';
      } }>(`/admin/hospitals/${hospitalId}`);
      const h = res.data;
      return {
        id: h.id,
        name: h.name,
        status: h.status as 'ACTIVE' | 'INACTIVE',
        activeDoctors: h.activeDoctorCount,
        totalDoctors: h.doctorCount,
        activeSessions: h.activeSessionCount,
        registrationUrl: `${window.location.origin}/${h.slug}/register`,
        billingModel: (h.billingModel ?? 'CONSUMPTION') as 'CONSUMPTION' | 'SUBSCRIPTION',
      };
    },
    enabled: Boolean(hospitalId),
  });

  const handleCopyUrl = async () => {
    if (!hospital?.registrationUrl) return;
    await navigator.clipboard.writeText(hospital.registrationUrl);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Dashboard', to: '/admin/dashboard' },
    { label: hospital?.name ?? 'Loading…' },
  ];

  if (!hospitalId) {
    return (
      <AdminLayout>
        <p className="text-sm font-body text-clinical-text-muted">Invalid hospital ID.</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout breadcrumbs={breadcrumbs}>
      {/* ------------------------------------------------------------------ */}
      {/* Header                                                               */}
      {/* ------------------------------------------------------------------ */}
      <div className="mb-6">
        {isLoading ? (
          <div className="space-y-3">
            <div className="skeleton h-8 w-64 rounded" />
            <div className="skeleton h-4 w-96 rounded" />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <h1 className="font-heading text-2xl font-bold text-clinical-charcoal">
                {hospital?.name ?? 'Hospital'}
              </h1>
              {hospital && <StatusBadge status={hospital.status} />}
            </div>

            {/* Stats row */}
            <div className="flex flex-wrap items-center gap-6 text-sm font-body text-clinical-text-muted mb-4">
              <span>
                <strong className="text-clinical-charcoal">{hospital?.activeDoctors ?? 0}</strong>
                <span className="text-clinical-text-muted">/{hospital?.totalDoctors ?? 0}</span>
                {' '}active doctors
              </span>
              <span>
                <strong className={hospital?.activeSessions ? 'text-green-600' : 'text-clinical-charcoal'}>
                  {hospital?.activeSessions ?? 0}
                </strong>
                {' '}active sessions
              </span>
            </div>

            {/* Registration URL */}
            {hospital?.registrationUrl && (
              <div className="flex items-center gap-2 max-w-xl">
                <input
                  type="text"
                  readOnly
                  value={hospital.registrationUrl}
                  className="form-input font-mono text-xs flex-1 py-2 min-h-[36px]"
                />
                <button
                  type="button"
                  onClick={() => { void handleCopyUrl(); }}
                  className="btn-secondary text-xs px-3 py-2 min-h-[36px] flex-none gap-1.5"
                >
                  {copiedUrl ? (
                    <>
                      <svg className="w-3.5 h-3.5 text-clinical-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      Copied
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                      </svg>
                      Copy URL
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Tabs                                                                 */}
      {/* ------------------------------------------------------------------ */}
      <div>
        {/* Tab nav */}
        <div className="border-b border-clinical-border mb-6">
          <nav className="-mb-px flex gap-0 overflow-x-auto" aria-label="Hospital detail tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={[
                  'inline-flex items-center px-4 py-3 text-sm font-body font-medium whitespace-nowrap border-b-2 transition-colors duration-150',
                  activeTab === tab.id
                    ? 'border-clinical-teal text-clinical-teal'
                    : 'border-transparent text-clinical-text-muted hover:text-clinical-charcoal hover:border-clinical-border',
                ].join(' ')}
                aria-selected={activeTab === tab.id}
                role="tab"
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        <Suspense fallback={<TabSkeleton />}>
          {activeTab === 'doctors' && <DoctorManagementTab hospitalId={hospitalId} billingModel={hospital?.billingModel ?? 'CONSUMPTION'} />}
          {activeTab === 'sessions' && <LiveSessionsTab hospitalId={hospitalId} />}
          {activeTab === 'analytics' && <AnalyticsTab hospitalId={hospitalId} />}
          {activeTab === 'errors' && <ErrorLogsTab hospitalId={hospitalId} />}
          {activeTab === 'billing' && <BillingTab hospitalId={hospitalId} billingModel={hospital?.billingModel ?? 'CONSUMPTION'} />}
          {activeTab === 'settings' && (
            <SettingsTab hospitalId={hospitalId} onHospitalUpdated={() => void refetch()} />
          )}
          {activeTab === 'compliance' && <CompliancePrivacyTab hospitalId={hospitalId} />}
        </Suspense>
      </div>
    </AdminLayout>
  );
}
